import React, { useState, useEffect, useCallback } from 'react';
import './ProductModal.css';

const ProductModal = ({ isOpen, onClose, isUserAuthenticated = false, onAuthRequest }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    setIsVisible(isOpen);
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
      setSelectedProduct(null);
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setSelectedProduct(null);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  const handleProductClick = useCallback((productId) => {
    // Always show action area when product is clicked
    setSelectedProduct(productId);
  }, []);

  const handleExplore = useCallback((productId) => {
    const productUrls = {
      medicine: 'http://localhost:3000/Medicine-management', // Medicine Management local server
      license: 'https://quixent.com/license-plate-recognizer',
      firecrackers: 'https://quixent.com/firecrackers-management'
    };

    let targetUrl = productUrls[productId] || 'https://quixent.com';

    // For medicine management on localhost, append JWT token
    if (productId === 'medicine') {
      // Get JWT token from localStorage
      const jwtToken = localStorage.getItem('token') || 
                       localStorage.getItem('jwtToken') || 
                       localStorage.getItem('accessToken') ||
                       localStorage.getItem('authToken');
      
      if (jwtToken) {
        // Append JWT token as URL parameter
        targetUrl = `${targetUrl}?token=${encodeURIComponent(jwtToken)}`;
      } else {
        console.warn('No JWT token found for authentication');
      }
    }

    // Open product page in new tab
    window.open(targetUrl, '_blank');
    handleClose();
  }, [handleClose]);

  const handleLoginRequest = useCallback(() => {
    if (onAuthRequest) {
      onAuthRequest('login');
    }
    handleClose();
  }, [onAuthRequest, handleClose]);

  const handleSignupRequest = useCallback(() => {
    if (onAuthRequest) {
      onAuthRequest('signup');
    }
    handleClose();
  }, [onAuthRequest, handleClose]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  const handleEscapeKey = useCallback((e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  }, [handleClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, handleEscapeKey]);

  if (!isOpen) return null;

  const products = [
    {
      id: 'medicine',
      icon: '/quixent_updated_logo.png',
      title: 'Medicine Management',
      description: 'Comprehensive pharmaceutical tracking and inventory management system for healthcare providers.'
    },
    {
      id: 'license',
      icon: '/quixent_updated_logo.png',
      title: 'License Plate Recognizer',
      description: 'Advanced AI-powered vehicle identification and automated parking management solutions.'
    },
    {
      id: 'firecrackers',
      icon: '/quixent_updated_logo.png',
      title: 'Firecrackers Management',
      description: 'Safety compliance and inventory tracking system for pyrotechnic and explosive materials.'
    }
  ];

  return (
    <div 
      className={`product-modal-overlay ${isVisible ? 'active' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className="product-modal-container">
        <button className="product-close-btn" onClick={handleClose}>
          &times;
        </button>
        
        <div className="product-modal-header">
          <img 
            src="/quixent_updated_logo.png" 
            alt="Quixent Solutions" 
            className="company-logo"
            onError={(e) => {
              console.error('Logo failed to load, hiding element');
              e.target.style.display = 'none';
            }}
          />
          <h2>Welcome to Quixent Solutions!</h2>
          <p>Discover our innovative solutions designed to transform your business operations.</p>
        </div>

        <div className="products-grid">
          {products.map((product) => (
            <div key={product.id} className="product-card">
              <div className="product-content" onClick={() => handleProductClick(product.id)}>
                <div className={`product-icon ${product.id}-icon`}>
                  <img 
                    src={product.icon} 
                    alt={product.title} 
                    className="product-icon-image"
                  />
                </div>
                <div className="product-title">{product.title}</div>
                <div className="product-description">{product.description}</div>
              </div>
              
              {selectedProduct === product.id && (
                <div className="product-action-area">
                  {isUserAuthenticated ? (
                    <div className="explore-section">
                      <button 
                        className="explore-btn"
                        onClick={() => handleExplore(product.id)}
                      >
                        Explore Now →
                      </button>
                    </div>
                  ) : (
                    <div className="login-required-section">
                      <div className="login-message">
                        <p>🔐 Please login to access this product</p>
                      </div>
                      <div className="auth-buttons">
                        <button 
                          className="login-btn"
                          onClick={handleLoginRequest}
                        >
                          Login
                        </button>
                        <button 
                          className="signup-btn"
                          onClick={handleSignupRequest}
                        >
                          Sign Up
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductModal;